import React, { createContext, useContext, useEffect, useState } from "react";

interface DialogHistoryContextType {
    registerDialog: (onClose: () => void) => void;
    unregisterDialog: (onClose: () => void) => void;
}

const DialogHistoryContext = createContext<DialogHistoryContextType | undefined>(undefined);

export const DialogHistoryProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
    const [dialogStack, setDialogStack] = useState<(() => void)[]>([]);

    useEffect(() => {
        const handleBack = () => {
            if (dialogStack.length > 0) {
                const closeDialog = dialogStack[dialogStack.length - 1]; // Get last opened dialog
                setDialogStack((prev) => prev.slice(0, -1)); // Remove it from stack
                closeDialog(); // Close the dialog
            }
        };

        window.addEventListener("popstate", handleBack);
        return () => window.removeEventListener("popstate", handleBack);
    }, [dialogStack]);

    const registerDialog = (onClose: () => void) => {
        setDialogStack((prev) => [...prev, onClose]);
        window.history.pushState({ dialogOpen: true }, ""); // Add history entry
    };

    const unregisterDialog = (onClose: () => void) => {
        setDialogStack((prev) => prev.filter((fn) => fn !== onClose));
    };

    return (
        <DialogHistoryContext.Provider value={{ registerDialog, unregisterDialog }}>
            {children}
        </DialogHistoryContext.Provider>
    );
};

export const useDialogHistory = (): DialogHistoryContextType => {
    const context = useContext(DialogHistoryContext);
    if (!context) {
        throw new Error("useDialogHistory must be used within a DialogHistoryProvider");
    }
    return context;
};
